import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Typography, Divider } from '@mui/material';
import { useSettings } from '../../providers/settings-provider';

type Transaction = { guid: string; description?: string; date: string | Date; amount: number; originalAmount?: number; type?: string };

// Mirrors buildSumsByCategory's rule (forecast-groups.ts): a provision's own
// `amount` gets reduced (often to 0) once a real transaction links/settles
// against it, so the cell total above this modal sums `originalAmount`
// instead for provisions - this modal's own totals/rows need the same
// substitution or they'd silently disagree with the cell they were opened from.
const effectiveAmount = (t: Transaction): number => Number((t.type === 'provision' ? (t.originalAmount ?? t.amount) : t.amount) || 0);

type SimulationCellDetailProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    // Forecast side (provisions) - always shown.
    forecastBaseline: Transaction[];
    forecastSimulated: Transaction[];
    // Actual side (real transactions) - only passed on the Forecast vs Actual page;
    // omitted entirely on the Forecast-only page, where there's no Actual to show.
    actualBaseline?: Transaction[];
    actualSimulated?: Transaction[];
    // When provided, shows an "Edit in Simulation" button that hands off to the
    // caller to open the simulation editor scoped to this cell's provisions.
    onEditInSimulation?: () => void;
};

const diffTransactions = (baseline: Transaction[], simulated: Transaction[]) => {
    const baselineGuids = new Set(baseline.map(t => t.guid));
    const simulatedGuids = new Set(simulated.map(t => t.guid));
    const removed = baseline.filter(t => !simulatedGuids.has(t.guid));
    const added = simulated.filter(t => !baselineGuids.has(t.guid));
    const unchanged = simulated.filter(t => baselineGuids.has(t.guid));
    const baselineTotal = baseline.reduce((s, t) => s + effectiveAmount(t), 0);
    const simulatedTotal = simulated.reduce((s, t) => s + effectiveAmount(t), 0);
    return { removed, added, unchanged, baselineTotal, simulatedTotal, difference: simulatedTotal - baselineTotal };
};

// Shows what an applied simulation actually did to one cell's underlying data:
// transactions it removed, transactions it added (from move/defer rules), and
// transactions it left untouched. On the Forecast vs Actual page this covers
// both sides separately (a simulation can change provisions, real transactions,
// or both), since lumping their totals together would mix two different measures.
const SimulationCellDetail: React.FC<SimulationCellDetailProps> = ({
    open, onClose, title, forecastBaseline, forecastSimulated, actualBaseline, actualSimulated, onEditInSimulation,
}) => {
    const { formatAmount, formatDate } = useSettings();
    const showActual = actualBaseline !== undefined && actualSimulated !== undefined;

    const txRow = (t: Transaction, strike: boolean, bold: boolean) => (
        <Box key={t.guid} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: '1px solid #eee' }}>
            <Typography fontSize={13} sx={{ textDecoration: strike ? 'line-through' : 'none', color: strike ? 'text.secondary' : 'inherit', fontWeight: bold ? 600 : 400 }}>
                {t.description || 'No description'} — {formatDate?.(t.date) ?? new Date(t.date).toLocaleDateString()}
            </Typography>
            <Typography fontSize={13} sx={{ textDecoration: strike ? 'line-through' : 'none', color: strike ? 'text.secondary' : 'inherit', fontWeight: bold ? 600 : 400, whiteSpace: 'nowrap' }}>
                {formatAmount?.(effectiveAmount(t))}
            </Typography>
        </Box>
    );

    const renderSection = (label: string, baseline: Transaction[], simulated: Transaction[]) => {
        const { removed, added, unchanged, baselineTotal, simulatedTotal, difference } = diffTransactions(baseline, simulated);
        return (
            <Box mb={2}>
                <Typography fontWeight={700} fontSize={12} mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-muted)' }}>{label}</Typography>
                <Box display="flex" gap={3} mb={1.5}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Baseline</Typography>
                        <Typography fontWeight={600}>{formatAmount?.(baselineTotal)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Simulated</Typography>
                        <Typography fontWeight={600}>{formatAmount?.(simulatedTotal)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Difference</Typography>
                        <Typography fontWeight={600} color={difference < 0 ? 'var(--color-negative-amount)' : difference > 0 ? '#2e7d32' : 'inherit'}>
                            {formatAmount?.(difference)}
                        </Typography>
                    </Box>
                </Box>

                {removed.length > 0 && (
                    <Box mb={1.5}>
                        <Typography fontWeight={600} fontSize={13} color="var(--color-negative-amount)" mb={0.5}>Removed by simulation</Typography>
                        {removed.map(t => txRow(t, true, false))}
                    </Box>
                )}

                {added.length > 0 && (
                    <Box mb={1.5}>
                        <Typography fontWeight={600} fontSize={13} color="#2e7d32" mb={0.5}>Added by simulation</Typography>
                        {added.map(t => txRow(t, false, true))}
                    </Box>
                )}

                {unchanged.length > 0 && (
                    <Box mb={1.5}>
                        <Typography fontWeight={600} fontSize={13} mb={0.5}>Unchanged</Typography>
                        {unchanged.map(t => txRow(t, false, false))}
                    </Box>
                )}

                {removed.length === 0 && added.length === 0 && (
                    <Typography fontSize={13} color="text.secondary">No change in this section.</Typography>
                )}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Typography fontWeight={600} fontSize={16}>{title}</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                {renderSection('Forecast', forecastBaseline, forecastSimulated)}
                {showActual && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        {renderSection('Actual', actualBaseline!, actualSimulated!)}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: onEditInSimulation ? 'space-between' : 'flex-end' }}>
                {onEditInSimulation && (
                    <Button onClick={onEditInSimulation} variant="outlined" sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
                        Edit in Simulation
                    </Button>
                )}
                <Button onClick={onClose} variant="contained" sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SimulationCellDetail;
